/**
 * Boîte de réception de l'organisme (29/10/2026) : les messages envoyés depuis le site public skills4mation.com.
 * Onglets Réclamations / Contacts / Pré-inscriptions / Droits formation, pastille des nouveaux messages, et fiche de
 * traitement d'une réclamation conforme à l'indicateur 31 Qualiopi (nature, accusé de réception, réponse apportée,
 * action corrective, qui et quand, historique).
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCheck, Download, Hourglass, Inbox, Mail, Phone, ShieldCheck, Timer, UserX } from "lucide-react";
import { api, dateFr, instantFr, type CompteursBoite, type IndicateurReclamations, type LigneMessage, type MessageEntrant } from "../api";
import {
  Indicateur,
  Alerte,
  Bouton,
  Carte,
  Champ,
  Chargement,
  EtatVide,
  Etiquette,
  Modale,
  Onglets,
  Selecteur,
  TitrePage,
  ZoneTexte,
  useNotifier,
} from "../ui/base";

type Type = LigneMessage["type"];
type Statut = LigneMessage["statut"];

const ONGLETS: Array<{ cle: Type; libelle: string; vide: string }> = [
  {
    cle: "reclamation",
    libelle: "Réclamations",
    vide: "Les réclamations, difficultés et aléas signalés sur la page « Réclamation » du site arriveront ici.",
  },
  {
    cle: "contact",
    libelle: "Contacts",
    vide: "Les demandes du formulaire de contact et du volet « Rejoindre Skills4mation » arriveront ici.",
  },
  {
    cle: "preinscription",
    libelle: "Pré-inscriptions",
    vide: "Les pré-inscriptions envoyées depuis les fiches formation du catalogue arriveront ici.",
  },
  {
    cle: "droits_formation",
    libelle: "Droits formation",
    vide: "Les demandes « Mes droits formation » (diagnostic express) arriveront ici.",
  },
  {
    cle: "ressource",
    libelle: "Ressources",
    vide: "Les demandes de livre blanc (e-mail et consentement) envoyées depuis la page « Livre blanc » arriveront ici.",
  },
];

const STATUTS: Record<Statut, { libelle: string; ton: "neutre" | "attente" | "accent" | "danger" }> = {
  nouveau: { libelle: "Nouveau", ton: "attente" },
  en_cours: { libelle: "En cours", ton: "neutre" },
  traite: { libelle: "Traité", ton: "accent" },
};

const NATURES: Record<string, string> = {
  reclamation: "Réclamation",
  difficulte: "Difficulté rencontrée",
  alea: "Aléa (santé, planning, technique…)",
  appreciation: "Appréciation / suggestion",
};

/** Libellés des champs propres à chaque formulaire (`donnees`). */
const CHAMPS: Record<string, string> = {
  formation_concernee: "Formation concernée",
  profil: "Profil",
  formation_souhaitee: "Formation souhaitée",
  objectif: "Objectif",
  disponibilites: "Disponibilités",
  budget_estime: "Budget",
  financement: "Financement",
  formation_titre: "Formation",
  formation_slug: "Fiche du catalogue",
  formation_ref: "Réf. catalogue",
  formateur_ref: "Réf. formateur",
  statut_pro: "Statut professionnel",
  situation: "Situation",
  formation_visee: "Formation visée",
  objectif_professionnel: "Objectif professionnel",
  entreprise_nom: "Entreprise employeuse",
  entreprise_siret: "SIRET",
  recapitulatif: "Estimation affichée",
  ressource: "Ressource",
  ressource_titre: "Titre de la ressource",
  consentement: "Consentement (envoi de la ressource)",
  newsletter: "Prochains guides par e-mail",
};

/** Délai annoncé sur la page « Réclamation » du site. */
const DELAI_RECLAMATION_JOURS_OUVRES = 15;

/** Jours ouvrés (lundi-vendredi) écoulés entre deux instants — indicateur de délai, hors jours fériés. */
export function joursOuvres(depuis: Date, jusqua: Date): number {
  let n = 0;
  const d = new Date(depuis);
  d.setHours(12, 0, 0, 0);
  const fin = new Date(jusqua);
  fin.setHours(12, 0, 0, 0);
  while (d < fin) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) n++;
  }
  return n;
}

export function BoiteReception() {
  const [onglet, setOnglet] = useState<Type>("reclamation");
  const [statut, setStatut] = useState<"" | Statut>("");
  const [recherche, setRecherche] = useState("");
  const [ouvert, setOuvert] = useState<string | null>(null);

  // Lien du courriel de notification : /admin/boite-de-reception?message=<id> ouvre directement la fiche.
  useEffect(() => {
    const id = new URLSearchParams(location.search).get("message");
    if (id) setOuvert(id);
  }, []);

  const parametres = new URLSearchParams({
    type: onglet,
    ...(statut ? { statut } : {}),
    ...(recherche.trim() ? { q: recherche.trim() } : {}),
  }).toString();
  const liste = useQuery({
    queryKey: ["boite-reception", parametres],
    queryFn: () => api.get<LigneMessage[]>(`/admin/boite-reception?${parametres}`),
  });
  const compteurs = useQuery({
    queryKey: ["boite-reception-compteurs"],
    queryFn: () => api.get<CompteursBoite>("/admin/boite-reception/compteurs"),
  });
  const actif = ONGLETS.find((o) => o.cle === onglet)!;

  return (
    <>
      <TitrePage
        titre="Boîte de réception"
        soustitre={
          compteurs.data && compteurs.data.total > 0
            ? `${compteurs.data.total} nouveau(x) message(s) reçu(s) depuis le site skills4mation.com.`
            : "Les messages envoyés depuis le site skills4mation.com : réclamations, demandes de contact, pré-inscriptions, droits formation."
        }
        actions={
          <a
            href={`/api/admin/boite-reception/export?${parametres}`}
            className="inline-flex h-10 items-center gap-2 rounded-sm border border-trait-fort bg-carte px-4 text-sm font-medium hover:bg-papier-2"
          >
            <Download className="size-4" aria-hidden />{" "}
            {onglet === "reclamation" ? "Registre des réclamations (CSV)" : "Exporter (CSV)"}
          </a>
        }
      />
      <Onglets
        actif={onglet}
        choisir={(c) => {
          setOnglet(c);
          setStatut("");
        }}
        onglets={ONGLETS.map((o) => ({
          cle: o.cle,
          libelle: o.libelle,
          compteur: compteurs.data?.[o.cle] ? String(compteurs.data[o.cle]) : undefined,
        }))}
      />
      {onglet === "reclamation" && <DelaiReclamations />}
      <div className="mb-4 grid gap-3 sm:grid-cols-[200px_minmax(0,1fr)]">
        <Selecteur libelle="Statut" value={statut} onChange={(e) => setStatut(e.target.value as "" | Statut)}>
          <option value="">Tous</option>
          {Object.entries(STATUTS).map(([k, v]) => (
            <option key={k} value={k}>
              {v.libelle}
            </option>
          ))}
        </Selecteur>
        <Champ
          libelle="Rechercher"
          placeholder="Nom, e-mail, numéro, objet…"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
        />
      </div>
      {liste.isPending ? (
        <Chargement />
      ) : liste.error ? (
        <Alerte ton="danger">{liste.error.message}</Alerte>
      ) : liste.data.length === 0 ? (
        <EtatVide
          icone={<Inbox className="size-8" aria-hidden />}
          titre={statut || recherche ? "Aucun message ne correspond" : "Aucun message"}
        >
          {statut || recherche ? "Modifiez le statut ou la recherche." : actif.vide}
        </EtatVide>
      ) : (
        <Carte>
          <ul className="divide-y divide-trait">
            {liste.data.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => setOuvert(m.id)}
                  className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3.5 text-left hover:bg-papier-2 sm:px-5"
                >
                  <span className="min-w-0">
                    <span className={m.statut === "nouveau" ? "block font-semibold" : "block font-medium"}>
                      {[m.prenom, m.nom].filter(Boolean).join(" ") || "—"}
                      {m.type === "reclamation" && m.nature && (
                        <span className="ml-2 text-[13px] font-normal text-encre-3">
                          {NATURES[m.nature] ?? m.nature}
                        </span>
                      )}
                    </span>
                    <span className="block truncate text-[13px] text-encre-2">
                      {[m.sujet, m.email].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="chiffres text-xs text-encre-3">
                      {m.numero} · {instantFr(m.cree_le)}
                    </span>
                    <Etiquette ton={STATUTS[m.statut].ton}>{STATUTS[m.statut].libelle}</Etiquette>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Carte>
      )}
      <Modale
        ouverte={ouvert !== null}
        fermer={() => {
          setOuvert(null);
          if (location.search.includes("message=")) history.replaceState(null, "", location.pathname);
        }}
        titre="Message reçu"
        large
      >
        {ouvert && <Fiche id={ouvert} />}
      </Modale>
    </>
  );
}

function Fiche({ id }: { id: string }) {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const m = useQuery({
    queryKey: ["message-entrant", id],
    queryFn: () => api.get<MessageEntrant>(`/admin/boite-reception/${id}`),
  });
  const rafraichir = async (maj: MessageEntrant) => {
    requetes.setQueryData(["message-entrant", id], maj);
    await requetes.invalidateQueries({ queryKey: ["boite-reception"] });
    await requetes.invalidateQueries({ queryKey: ["boite-reception-compteurs"] });
  };
  const statut = useMutation({
    mutationFn: (s: Statut) =>
      api.patch<MessageEntrant>(`/admin/boite-reception/${id}/statut`, { statut: s }),
    onSuccess: async (maj) => {
      await rafraichir(maj);
      notifier("succes", `Statut : ${STATUTS[maj.statut].libelle.toLowerCase()}.`);
    },
  });
  const anonymiser = useMutation({
    mutationFn: () => api.post<MessageEntrant>(`/admin/boite-reception/${id}/anonymiser`),
    onSuccess: async (maj) => {
      await rafraichir(maj);
      notifier("succes", "Données personnelles effacées. La trace du traitement est conservée.");
    },
  });

  if (m.isPending) return <Chargement />;
  if (m.error) return <Alerte ton="danger">{m.error.message}</Alerte>;
  const d = m.data;
  const details = Object.entries(d.donnees).filter(([k]) => k !== "formation_ref" && k !== "formateur_ref");

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="min-w-0 space-y-4">
        <div>
          <p className="chiffres text-xs text-encre-3">
            {d.numero} · reçu le {instantFr(d.cree_le)}
            {d.page_origine ? ` · page ${d.page_origine}` : ""}
          </p>
          <p className="font-display text-xl font-semibold">
            {[d.prenom, d.nom].filter(Boolean).join(" ") || "—"}
          </p>
          <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-encre-2">
            {d.email && (
              <a
                href={`mailto:${d.email}?subject=${encodeURIComponent(`Votre message ${d.numero}`)}`}
                className="inline-flex items-center gap-1.5 hover:text-accent-fort"
              >
                <Mail className="size-3.5" aria-hidden /> {d.email}
              </a>
            )}
            {d.telephone && (
              <a
                href={`tel:${d.telephone.replace(/\s+/g, "")}`}
                className="inline-flex items-center gap-1.5 hover:text-accent-fort"
              >
                <Phone className="size-3.5" aria-hidden /> {d.telephone}
              </a>
            )}
          </p>
        </div>
        {d.sujet && (
          <p className="text-sm">
            <span className="text-encre-3">Objet : </span>
            {d.sujet}
          </p>
        )}
        {d.message && (
          <p className="rounded-md bg-papier-2 p-3 text-sm leading-relaxed whitespace-pre-line">
            {d.message}
          </p>
        )}
        {details.length > 0 && (
          <dl className="space-y-1.5 text-sm">
            {details.map(([k, v]) => (
              <div key={k} className="grid grid-cols-[160px_minmax(0,1fr)] gap-3">
                <dt className="text-encre-3">{CHAMPS[k] ?? k}</dt>
                <dd className="whitespace-pre-line">{v}</dd>
              </div>
            ))}
          </dl>
        )}
        {d.notification === "echec" && (
          <Alerte ton="attention">
            La notification e-mail de ce message a échoué : vérifiez les réglages d'envoi (Organisme →
            E-mails).
          </Alerte>
        )}
        <Historique entrees={d.historique} />
      </div>
      <div className="min-w-0 space-y-4">
        {d.anonymise_le ? (
          <Alerte ton="info" titre="Message anonymisé">
            Les données personnelles ont été effacées le {dateFr(d.anonymise_le)}. La trace du traitement est
            conservée.
          </Alerte>
        ) : d.type === "reclamation" ? (
          <FicheReclamation key={d.maj_le} message={d} rafraichir={rafraichir} />
        ) : (
          <SuiteDonnee key={d.maj_le} message={d} rafraichir={rafraichir} />
        )}
        {!d.anonymise_le && (
          <div className="flex flex-wrap items-center gap-2 border-t border-trait pt-4">
            {d.type !== "reclamation" &&
              (Object.keys(STATUTS) as Statut[])
                .filter((s) => s !== d.statut)
                .map((s) => (
                  <Bouton
                    key={s}
                    taille="sm"
                    enCours={statut.isPending && statut.variables === s}
                    onClick={() => statut.mutate(s)}
                  >
                    Marquer « {STATUTS[s].libelle.toLowerCase()} »
                  </Bouton>
                ))}
            <Bouton
              taille="sm"
              variante="discret"
              icone={<UserX className="size-4" aria-hidden />}
              enCours={anonymiser.isPending}
              onClick={() => {
                if (
                  confirm(
                    "Effacer les données personnelles de ce message (droit à l'effacement) ? Cette action est définitive.",
                  )
                )
                  anonymiser.mutate();
              }}
            >
              Effacer les données personnelles
            </Bouton>
          </div>
        )}
        {(statut.error || anonymiser.error) && (
          <Alerte ton="danger">{(statut.error ?? anonymiser.error)!.message}</Alerte>
        )}
      </div>
    </div>
  );
}

/** Fiche de traitement d'une réclamation — indicateur 31 du référentiel national qualité (Qualiopi). */
function FicheReclamation({
  message: d,
  rafraichir,
}: {
  message: MessageEntrant;
  rafraichir: (m: MessageEntrant) => Promise<void>;
}) {
  const notifier = useNotifier();
  const [nature, setNature] = useState(d.nature || "reclamation");
  const [accuse, setAccuse] = useState(d.accuse_reception_le?.slice(0, 10) ?? "");
  const [reponse, setReponse] = useState(d.reponse_apportee);
  const [action, setAction] = useState(d.action_corrective);
  const enregistrer = useMutation({
    mutationFn: (statut?: Statut) =>
      api.patch<MessageEntrant>(`/admin/boite-reception/${d.id}/traitement`, {
        nature,
        accuse_reception_le: accuse,
        reponse_apportee: reponse,
        action_corrective: action,
        ...(statut ? { statut } : {}),
      }),
    onSuccess: async (maj, statut) => {
      await rafraichir(maj);
      notifier(
        "succes",
        statut === "traite"
          ? "Réclamation close. Elle figure au registre avec sa réponse et son action corrective."
          : "Traitement enregistré.",
      );
    },
  });
  const jours = joursOuvres(new Date(d.cree_le), d.traite_le ? new Date(d.traite_le) : new Date());
  const enRetard = d.statut !== "traite" && jours > DELAI_RECLAMATION_JOURS_OUVRES;
  const erreurs =
    enregistrer.error && "details" in enregistrer.error
      ? (enregistrer.error.details as { champs?: Record<string, string> } | null)?.champs
      : undefined;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-medium">
            <ShieldCheck className="size-4 text-accent-fort" aria-hidden /> Traitement de la réclamation
          </p>
          <p className="text-[13px] text-encre-3">
            Indicateur 31 Qualiopi : nature, réponse apportée, action corrective et traçabilité.
          </p>
        </div>
        <Etiquette ton={STATUTS[d.statut].ton}>{STATUTS[d.statut].libelle}</Etiquette>
      </div>
      <Alerte ton={enRetard ? "attention" : "info"}>
        {d.statut === "traite"
          ? `Traitée en ${jours} jour(s) ouvré(s), le ${dateFr(d.traite_le)} par ${d.traite_par_nom || "—"}.`
          : `${jours} jour(s) ouvré(s) depuis la réception — délai annoncé sur le site : ${DELAI_RECLAMATION_JOURS_OUVRES} jours ouvrés.`}
      </Alerte>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Selecteur libelle="Nature" value={nature} onChange={(e) => setNature(e.target.value)}>
          {Object.entries(NATURES).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Selecteur>
        <Champ
          libelle="Accusé de réception envoyé le"
          type="date"
          value={accuse}
          onChange={(e) => setAccuse(e.target.value)}
        />
      </div>
      <ZoneTexte
        libelle="Réponse apportée"
        aide="Ce qui a été répondu ou fait pour la personne (obligatoire pour clore)."
        rows={4}
        value={reponse}
        onChange={(e) => setReponse(e.target.value)}
        erreur={erreurs?.reponse_apportee}
      />
      <ZoneTexte
        libelle="Action corrective"
        aide="Ce qui change pour que la situation ne se reproduise pas (alimente l'amélioration continue, indicateur 32)."
        rows={3}
        value={action}
        onChange={(e) => setAction(e.target.value)}
      />
      {enregistrer.error && !erreurs && <Alerte ton="danger">{enregistrer.error.message}</Alerte>}
      <div className="flex flex-wrap gap-2">
        <Bouton
          enCours={enregistrer.isPending && enregistrer.variables === undefined}
          onClick={() => enregistrer.mutate(undefined)}
        >
          Enregistrer
        </Bouton>
        {d.statut === "traite" ? (
          <Bouton
            enCours={enregistrer.isPending && enregistrer.variables === "en_cours"}
            onClick={() => enregistrer.mutate("en_cours")}
          >
            Rouvrir
          </Bouton>
        ) : (
          <Bouton
            variante="primaire"
            enCours={enregistrer.isPending && enregistrer.variables === "traite"}
            onClick={() => enregistrer.mutate("traite")}
          >
            Clore la réclamation
          </Bouton>
        )}
      </div>
    </div>
  );
}

/** Suite donnée à un contact, une pré-inscription ou une demande « droits formation ». */
function SuiteDonnee({
  message: d,
  rafraichir,
}: {
  message: MessageEntrant;
  rafraichir: (m: MessageEntrant) => Promise<void>;
}) {
  const notifier = useNotifier();
  const [suite, setSuite] = useState(d.reponse_apportee);
  const enregistrer = useMutation({
    mutationFn: () =>
      api.patch<MessageEntrant>(`/admin/boite-reception/${d.id}/traitement`, { reponse_apportee: suite }),
    onSuccess: async (maj) => {
      await rafraichir(maj);
      notifier("succes", "Suite donnée enregistrée.");
    },
  });
  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="font-medium">Suite donnée</p>
        <Etiquette ton={STATUTS[d.statut].ton}>{STATUTS[d.statut].libelle}</Etiquette>
      </div>
      <ZoneTexte
        libelle="Note de suivi"
        aide="Rappel effectué, devis envoyé, orientation… (visible des seuls administrateurs)."
        rows={4}
        value={suite}
        onChange={(e) => setSuite(e.target.value)}
      />
      {enregistrer.error && <Alerte ton="danger">{enregistrer.error.message}</Alerte>}
      <Bouton
        enCours={enregistrer.isPending}
        onClick={() => enregistrer.mutate()}
        disabled={suite === d.reponse_apportee}
      >
        Enregistrer la note
      </Bouton>
      {d.traite_le && (
        <p className="text-[13px] text-encre-3">
          Traité le {dateFr(d.traite_le)} par {d.traite_par_nom || "—"}.
        </p>
      )}
    </div>
  );
}

function Historique({ entrees }: { entrees: MessageEntrant["historique"] }) {
  return (
    <div>
      <p className="mb-2 text-[13px] font-medium text-encre-3">Historique</p>
      <ol className="space-y-1.5 border-l border-trait pl-4 text-[13px]">
        {entrees.map((h, i) => (
          <li key={i}>
            <span className="chiffres text-encre-3">{instantFr(h.le)}</span> — {h.action}
            <span className="text-encre-3"> ({h.par})</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

const jours = (n: number | null) => (n === null ? "—" : `${String(n).replace(".", ",")} j`);

/**
 * Lot 1.4 — Qualiopi, indicateur 31 : délai moyen de traitement des réclamations, en gros chiffres, avec la méthode
 * de calcul affichée (comme pour la satisfaction, indicateur 2).
 */
function DelaiReclamations() {
  const ind = useQuery({ queryKey: ["boite-reception", "indicateur-31"], queryFn: () => api.get<IndicateurReclamations>("/admin/boite-reception/indicateur-reclamations") });
  if (!ind.data) return null;
  const r = ind.data;
  return (
    <section aria-labelledby="titre-ind-31" className="mb-5">
      <h2 id="titre-ind-31" className="mb-2 text-sm font-semibold text-encre-2">
        Suivi des réclamations — Qualiopi, indicateur 31 <span className="font-normal text-encre-3">(12 derniers mois)</span>
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Indicateur libelle="Délai moyen de traitement" valeur={jours(r.delai_moyen_jours)} detail={r.delai_max_jours === null ? "Aucune réclamation traitée sur la période" : `Le plus long : ${jours(r.delai_max_jours)}`} icone={<Timer className="size-4" aria-hidden />} ton="accent" />
        <Indicateur libelle="Réclamations traitées" valeur={`${r.traitees}/${r.recues}`} detail="traitées / reçues" icone={<CheckCheck className="size-4" aria-hidden />} />
        <Indicateur libelle="Encore ouvertes" valeur={r.ouvertes} detail={r.plus_ancienne_ouverte_jours === null ? "Rien en attente" : `La plus ancienne attend depuis ${r.plus_ancienne_ouverte_jours} j`} icone={<Hourglass className="size-4" aria-hidden />} ton={r.ouvertes > 0 ? "attente" : "neutre"} />
      </div>
      <details className="mt-2 text-[13px] text-encre-2">
        <summary className="cursor-pointer font-medium text-encre-2 hover:text-encre">Méthode de calcul</summary>
        <p className="mt-1 max-w-[80ch]">
          Réclamations reçues sur les {r.periode_jours} derniers jours. Délai d'une réclamation = date de passage au statut « Traité » moins date de réception : durée écoulée, exprimée en jours (24 h = 1 jour) ; délai moyen = moyenne de ces délais, arrondie au dixième. Les réclamations encore ouvertes sont comptées à part.
        </p>
      </details>
    </section>
  );
}
